import { GPU } from '@/data/gpuData';
import { X, Scale } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';

interface CompareModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  gpus: GPU[];
  onRemoveGPU: (gpuId: string) => void;
}

const brandColors = {
  nvidia: 'border-nvidia/50 bg-nvidia/5',
  amd: 'border-amd/50 bg-amd/5',
  intel: 'border-intel/50 bg-intel/5',
};

const brandTextColors = {
  nvidia: 'text-nvidia',
  amd: 'text-amd',
  intel: 'text-intel',
};

const specRows = [
  { key: 'performance', label: 'Performance', format: (gpu: GPU) => `${gpu.performance}%`, highlight: true },
  { key: 'vram', label: 'VRAM', format: (gpu: GPU) => gpu.vram },
  { key: 'msrp', label: 'MSRP', format: (gpu: GPU) => `$${gpu.msrp}` },
  { key: 'tdp', label: 'TDP', format: (gpu: GPU) => `${gpu.tdp}W` },
  { key: 'architecture', label: 'Architecture', format: (gpu: GPU) => gpu.architecture },
  { key: 'releaseYear', label: 'Release Year', format: (gpu: GPU) => gpu.releaseYear.toString() },
  { key: 'value', label: 'Value (Perf/$)', format: (gpu: GPU) => (gpu.performance / gpu.msrp * 100).toFixed(2), highlight: true },
  { key: 'efficiency', label: 'Efficiency (Perf/W)', format: (gpu: GPU) => (gpu.performance / gpu.tdp).toFixed(2), highlight: true },
];

export const CompareModal = ({ open, onOpenChange, gpus, onRemoveGPU }: CompareModalProps) => {
  // Find best values for highlighting
  const getBestValue = (key: string) => {
    if (key === 'performance' || key === 'value' || key === 'efficiency') {
      return Math.max(...gpus.map(g => {
        if (key === 'performance') return g.performance;
        if (key === 'value') return g.performance / g.msrp * 100;
        return g.performance / g.tdp;
      }));
    }
    if (key === 'msrp' || key === 'tdp') {
      return Math.min(...gpus.map(g => key === 'msrp' ? g.msrp : g.tdp));
    }
    return null;
  };

  const isCurrentBest = (gpu: GPU, key: string) => {
    const best = getBestValue(key);
    if (best === null) return false;
    
    if (key === 'performance') return gpu.performance === best;
    if (key === 'value') return (gpu.performance / gpu.msrp * 100) === best;
    if (key === 'efficiency') return (gpu.performance / gpu.tdp) === best;
    if (key === 'msrp') return gpu.msrp === best;
    if (key === 'tdp') return gpu.tdp === best;
    return false;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-auto bg-card">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Scale className="w-5 h-5" />
            GPU Comparison
          </DialogTitle>
        </DialogHeader>

        <div className="mt-4">
          {/* GPU Headers */}
          <div className="grid gap-4" style={{ gridTemplateColumns: `150px repeat(${gpus.length}, 1fr)` }}>
            <div /> {/* Empty corner cell */}
            {gpus.map((gpu) => (
              <div
                key={gpu.id}
                className={cn(
                  'p-4 rounded-lg border-2 text-center relative',
                  brandColors[gpu.brand]
                )}
              >
                <button
                  onClick={() => onRemoveGPU(gpu.id)}
                  className="absolute top-2 right-2 p-1 rounded-full hover:bg-background/50 transition-colors"
                  aria-label={`Remove ${gpu.name}`}
                >
                  <X className="w-4 h-4 text-muted-foreground" />
                </button>
                <div className={cn('text-xs font-semibold uppercase mb-1', brandTextColors[gpu.brand])}>
                  {gpu.brand}
                </div>
                <h3 className="font-bold text-lg">{gpu.name}</h3>
              </div>
            ))}
          </div>

          {/* Spec Rows */}
          <div className="mt-6 space-y-1">
            {specRows.map((row, idx) => (
              <div
                key={row.key}
                className={cn(
                  'grid gap-4 py-3 px-2 rounded-lg',
                  idx % 2 === 0 ? 'bg-muted/30' : ''
                )}
                style={{ gridTemplateColumns: `150px repeat(${gpus.length}, 1fr)` }}
              >
                <div className="font-medium text-sm text-muted-foreground flex items-center">
                  {row.label}
                </div>
                {gpus.map((gpu) => {
                  const isBest = isCurrentBest(gpu, row.key);
                  return (
                    <div
                      key={gpu.id}
                      className={cn(
                        'text-center font-medium',
                        row.highlight && isBest && 'text-primary font-bold',
                        row.highlight && isBest && 'relative'
                      )}
                    >
                      {row.format(gpu)}
                      {row.highlight && isBest && (
                        <span className="absolute -top-1 -right-1 w-2 h-2 bg-primary rounded-full" />
                      )}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>

          {/* Performance Bar Visualization */}
          <div className="mt-8 p-4 bg-muted/30 rounded-lg">
            <h4 className="font-medium text-sm text-muted-foreground mb-4">Relative Performance</h4>
            <div className="space-y-3">
              {gpus.map((gpu) => (
                <div key={gpu.id} className="flex items-center gap-3">
                  <span className="w-24 text-sm font-medium truncate">{gpu.name}</span>
                  <div className="flex-1 h-6 bg-muted rounded-full overflow-hidden">
                    <div
                      className={cn(
                        'h-full rounded-full transition-all duration-500 flex items-center justify-end pr-2',
                        gpu.brand === 'nvidia' && 'bg-nvidia',
                        gpu.brand === 'amd' && 'bg-amd',
                        gpu.brand === 'intel' && 'bg-intel'
                      )}
                      style={{ width: `${Math.min(gpu.performance, 100)}%` }}
                    >
                      <span className="text-xs font-bold text-white">{gpu.performance}%</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Legend */}
          <div className="mt-4 text-xs text-muted-foreground text-center">
            <span className="inline-flex items-center gap-1">
              <span className="w-2 h-2 bg-primary rounded-full" />
              Best in category
            </span>
            <span className="mx-2">•</span>
            Performance relative to RTX 4090 (100%)
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

interface CompareBarProps {
  selectedGPUs: GPU[];
  onRemoveGPU: (gpuId: string) => void;
  onCompare: () => void;
  onClear: () => void;
}

export const CompareBar = ({ selectedGPUs, onRemoveGPU, onCompare, onClear }: CompareBarProps) => {
  if (selectedGPUs.length === 0) return null;

  return (
    <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 animate-fade-in">
      <div className="bg-card border shadow-lg rounded-2xl p-4 flex items-center gap-4">
        <div className="flex items-center gap-2">
          <Scale className="w-5 h-5 text-primary" />
          <span className="text-sm font-medium">Compare</span>
        </div>

        <div className="flex items-center gap-2">
          {selectedGPUs.map((gpu) => (
            <div
              key={gpu.id}
              className={cn(
                'px-3 py-1.5 rounded-full text-sm font-medium flex items-center gap-2 border',
                brandColors[gpu.brand]
              )}
            >
              <span>{gpu.name}</span>
              <button
                onClick={() => onRemoveGPU(gpu.id)}
                className="hover:bg-background/50 rounded-full p-0.5"
                aria-label={`Remove ${gpu.name} from comparison`}
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
          {selectedGPUs.length < 4 && (
            <span className="text-xs text-muted-foreground px-2">
              Select up to {4 - selectedGPUs.length} more
            </span>
          )}
        </div>

        <div className="flex items-center gap-2 ml-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={onClear}
            className="text-muted-foreground"
          >
            Clear
          </Button>
          <Button
            size="sm"
            onClick={onCompare}
            disabled={selectedGPUs.length < 2}
            className="bg-primary hover:bg-primary/90"
          >
            Compare ({selectedGPUs.length})
          </Button>
        </div>
      </div>
    </div>
  );
};
